import { tweenPromise } from '@Animation/utils'
import { KENO_NUMBER_STATUS, KenoNumber } from '../base/number'
import { SPACE_INVADERS_ATLAS_FRAMES, SPACE_INVADERS_TEXTURE_KEYS } from '.'
import { STANDARD_LARGE_NUMBER_RATIO } from '@Animation/constants'
import { ANIMATION_IDENTIFIERS } from '@Animation/types'

export class SpaceInvadersNumber extends KenoNumber {
  explosion1: Phaser.GameObjects.Image | null = null
  explosion2: Phaser.GameObjects.Image | null = null
  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    number: number,
    isPlayerSelected: boolean,
  ) {
    super(scene, x, y, number, isPlayerSelected, ANIMATION_IDENTIFIERS.SPACE_INVADERS)
  }

  getGlowAtlasFrame() {
    return SPACE_INVADERS_ATLAS_FRAMES.NUMBER_GLOW
  }

  getGlowAtlas() {
    return SPACE_INVADERS_TEXTURE_KEYS.ATLAS
  }

  createStatusTextureMap(): { [key in KENO_NUMBER_STATUS]: string } {
    return {
      [KENO_NUMBER_STATUS.DEFAULT]: SPACE_INVADERS_TEXTURE_KEYS.NUMBER_DEFAULT,
      [KENO_NUMBER_STATUS.SELECTED_PLAYER]: SPACE_INVADERS_TEXTURE_KEYS.NUMBER_PLAYER_SELECTED,
      [KENO_NUMBER_STATUS.SELECTED_SYSTEM]: SPACE_INVADERS_TEXTURE_KEYS.NUMBER_SYSTEM_SELECTED,
      [KENO_NUMBER_STATUS.SELECTED_HIT]: SPACE_INVADERS_TEXTURE_KEYS.NUMBER_HIT,
    }
  }

  async explode(): Promise<void> {
    this.explosion1 = this.scene.add.image(
      0,
      0,
      SPACE_INVADERS_TEXTURE_KEYS.ATLAS,
      SPACE_INVADERS_ATLAS_FRAMES.EXPLOSION_1,
    )
    this.explosion2 = this.scene.add.image(
      0,
      0,
      SPACE_INVADERS_TEXTURE_KEYS.ATLAS,
      SPACE_INVADERS_ATLAS_FRAMES.EXPLOSION_2,
    )

    this.explosion1.setOrigin(0.5, 0.5)
    this.explosion1.setAlpha(0)
    this.explosion1.setScale(0.3)

    this.explosion2.setAlpha(0)
    this.explosion2.setScale(0.3)
    this.explosion2.setOrigin(0.5, 0.5)

    this.add([this.explosion1, this.explosion2])

    const originalPosition = new Phaser.Math.Vector2(this.x, this.y)
    const newPosition = this.calculatePopPositionInFrame()

    this.sendToBack(this.explosion1)
    this.sendToBack(this.explosion2)

    const fadeIn1 = tweenPromise(this.scene, {
      targets: [this.explosion1],
      alpha: 1,
      duration: 100,
    })

    const fadeIn2 = tweenPromise(this.scene, {
      targets: [this.explosion2],
      alpha: 1,
      duration: 200,
      yoyo: true,
    })

    const grow = tweenPromise(this.scene, {
      targets: [this],
      x: newPosition.x,
      y: newPosition.y,
      scale: 1,
      duration: 500,
    })

    await Promise.all([fadeIn1(), fadeIn2(), grow()])

    const fadeOut1 = tweenPromise(this.scene, {
      targets: [this.explosion1],
      alpha: 0,
      duration: 400,
    })

    const fadeOut2 = tweenPromise(this.scene, {
      targets: [this.explosion2],
      alpha: 0,
      duration: 100,
    })

    const shrink = tweenPromise(this.scene, {
      targets: [this],
      x: originalPosition.x,
      y: originalPosition.y,
      scale: STANDARD_LARGE_NUMBER_RATIO,
      duration: 700,
    })

    await Promise.all([fadeOut1(), fadeOut2(), shrink()])
  }

  async skip() {
    await super.skip()
    this.explosion1?.setVisible(false)
    this.explosion2?.setVisible(false)
  }

  async transitionToSystemSelected(): Promise<void> {
    this.image.setTexture(
      SPACE_INVADERS_TEXTURE_KEYS.NUMBER_SYSTEM_SELECTED_TRANSITION,
      this.number - 1,
    )
    this.setScale(STANDARD_LARGE_NUMBER_RATIO)
    await this.explode()
    super.transitionToSystemSelected()
    this.setScale(1)
  }

  async transitionToSelectedHit() {
    this.image.setTexture(SPACE_INVADERS_TEXTURE_KEYS.NUMBER_HIT_TRANSITION, this.number - 1)
    this.setScale(STANDARD_LARGE_NUMBER_RATIO)
    await this.explode()
    super.transitionToSelectedHit()
    this.setScale(1)
  }
}
